import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import {
  GetAllInquiriesResponsePayload,
  Inquiry,
  InquiryListOptions,
  InquiryListRequestParams,
  InquiryStage,
  inquiryStages
} from ".";
import { useAccessControl } from "../../app/use-access-control";
import { callLcosService } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";
import { formatUtcInTimezone } from "../use-timezone-converter";
import { useTimezoneConverter } from "../use-timezone-converter";
import { inquiryQueryKeys, type InquiryListQueryKey } from "./query-keys";

export const useInquiryList = (
  {
    search = "",
    stages = [],
    serviceIds = [],
    stageStartFrom,
    stageStartTo,
    sortBy = "",
    limit = 10,
    pageIndex = 0
  }: InquiryListOptions = {},
  enabled = true
) => {
  const { getToken } = useAccessToken();
  const { activeCenterId } = useAccessControl();
  const { timezone } = useTimezoneConverter(activeCenterId);
  const [filteredCountPlaceholder, setFilteredCountPlaceholder] = useState(0);
  const offset = pageIndex * limit;
  const [previousInquiries, setPreviousInquiries] = useState<Inquiry[]>([]);

  const inquiryQuery = useQuery({
    // Keep queryKey as an array + inline object (not `list({...})`) so @tanstack/query/exhaustive-deps
    // can see the same identifiers used in queryFn.
    queryKey: [
      ...inquiryQueryKeys.base,
      "list",
      {
        centerId: activeCenterId,
        search,
        stages,
        serviceIds,
        stageStartFrom,
        stageStartTo,
        sortBy,
        limit,
        offset
      }
    ] as InquiryListQueryKey,
    queryFn: async () =>
      getAllInquiries(await getToken(), {
        centerId: activeCenterId,
        search,
        stages,
        serviceIds,
        stageStartFrom,
        stageStartTo,
        sortBy,
        limit,
        offset
      }),
    placeholderData: {
      inquiries: previousInquiries,
      filteredCount: filteredCountPlaceholder,
      stagesCount: Object.fromEntries(
        inquiryStages.map(status => [status, 0] as const)
      ) as GetAllInquiriesResponsePayload["stagesCount"]
    },
    select: useCallback(
      (data: GetAllInquiriesResponsePayload) => {
        return {
          ...data,
          inquiries: data.inquiries.map(inquiry => ({
            ...inquiry,
            stageStartDate: formatUtcInTimezone(inquiry.stageStartDate, timezone, "YYYY-MM-DD"),
            statusStartDate: formatUtcInTimezone(inquiry.statusStartDate, timezone, "YYYY-MM-DD")
          })),
          totalPages: Math.ceil(data.filteredCount / limit)
        };
      },
      [timezone, limit]
    ),
    enabled
  });

  useEffect(() => {
    if (inquiryQuery.data) {
      setPreviousInquiries(inquiryQuery.data.inquiries);
    }
  }, [inquiryQuery.data]);

  useEffect(() => {
    setFilteredCountPlaceholder(oldState => inquiryQuery.data?.filteredCount || oldState);
  }, [inquiryQuery.data?.filteredCount]);

  return {
    ...inquiryQuery,
    queryKey: [
      ...inquiryQueryKeys.base,
      "list",
      {
        centerId: activeCenterId,
        search,
        stages,
        serviceIds,
        stageStartFrom,
        stageStartTo,
        sortBy,
        limit,
        offset
      }
    ] as InquiryListQueryKey
  };
};

export const parseToInquiryStage = (stage: unknown): InquiryStage | null =>
  inquiryStages.includes(stage as InquiryStage) ? (stage as InquiryStage) : null;

const getAllInquiries = async (
  accessToken: string,
  {
    centerId,
    search,
    stages = [],
    serviceIds = [],
    stageStartFrom,
    stageStartTo,
    sortBy,
    limit,
    offset
  }: InquiryListRequestParams
) => {
  const params = new URLSearchParams();
  params.set("search", search ?? "");
  stages.forEach(stage => params.append("stage", stage));
  serviceIds.forEach(id => params.append("service_id", id));
  if (stageStartFrom) {
    params.set("stage_start_from", stageStartFrom);
  }
  if (stageStartTo) {
    params.set("stage_start_to", stageStartTo);
  }
  if (sortBy) {
    params.set("sort_by", sortBy);
  }
  params.set("limit", String(limit ?? ""));
  params.set("offset", String(offset ?? ""));
  const qs = params.toString();
  return callLcosService<GetAllInquiriesResponsePayload>(accessToken, `/api/inquiries/${centerId}?${qs}`, "GET");
};
